import { FlatPatientWithSummaryReports } from '@/store/modules/patient-with-summary-reports/getters'
import { fillDate } from '@/components/patient-stats/common/utils'
import moment from 'moment-timezone'
import * as R from 'ramda'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { USER_TIME_ZONE } from '@/utils/time'
import { customNoteRenderer } from '@/components/patient-stats/graphs/custom-renderers'
import { convertIncidents } from '@/components/patient-stats/graphs/timeline-chart-options/convert-incidents'
import SeriesScatter = echarts.EChartOption.SeriesScatter
import SeriesCustom = echarts.EChartOption.SeriesCustom
import SeriesBar = echarts.EChartOption.SeriesBar

const MIN_NOTES_HEIGHT = 3
const INCIDENT_SPACES = 1
const INCIDENT_LABEL_DISTANCE = 2
const INCIDENT_LABEL_PADDING = 3
const INCIDENT_SYMBOL_SIZE = 8

enum Z_INDEX {
  INCIDENT_BAR,
  INCIDENT_SCATTER,
  NOTES_SQUARE,
  BOTTOMS,
}

const GRID = [
  { x: '5%', y: '10%', width: '90%', height: '81.2%' },
  { x: '5%', y: '0%', width: '90%', height: '100%' },
]

export function buildTimelineChartOptions(options: {
  summaryReports: FlatPatientWithSummaryReports
  colors: any
  chartWidth: number
}): echarts.EChartOption<SeriesBar | SeriesCustom | SeriesScatter> {
  const { summaryReports, colors, chartWidth } = options
  if (!summaryReports) {
    return null
  }

  const timezone = summaryReports.timezone || USER_TIME_ZONE
  const starttimestamp = summaryReports.starttimestamp
  const endtimestamp = summaryReports.endtimestamp
  const resultStartTimestamp = summaryReports.resultStartTimestamp || starttimestamp
  const patientNotes = summaryReports.patientNotes || []
  const incidents = summaryReports.incidents || []
  const timestamps = [...fillDate(timezone, starttimestamp <= 1 ? resultStartTimestamp : starttimestamp, endtimestamp)]

  // convert notes
  const patientNotesWithStartOfDay = patientNotes
    .map(patientNote => ({
      ...patientNote,
      timestampStartOfDay: moment(patientNote.createdTimestamp)
        .tz(timezone)
        .startOf('day')
        .valueOf(),
    }))
    .sort((a, b) => a.createdTimestamp - b.createdTimestamp)

  const patientNotesGroupByDay = R.groupBy(p => String(p.timestampStartOfDay), patientNotesWithStartOfDay)
  const maxNotesInADay = Object.values(patientNotesGroupByDay).reduce(
    (acc, cur) => Math.max(cur.length, acc),
    MIN_NOTES_HEIGHT
  )
  const seriesNotes = Object.entries(patientNotesGroupByDay)
    .map(([timestampStartOfDay, values]) =>
      values.map((value, index) => [timestampStartOfDay, index, value, value.createdTimestamp])
    )
    .flat()

  // convert incidents
  const convertedIncidents = convertIncidents({
    incidents,
    chartWidth,
    startOfDayTimestamps: timestamps,
    timezone,
    grid: GRID[0],
    incidentLabelDistance: INCIDENT_LABEL_DISTANCE,
    incidentLabelPadding: INCIDENT_LABEL_PADDING,
    incidentSymbolSize: INCIDENT_SYMBOL_SIZE,
  })

  const incidentsGroupByDay = R.groupBy(inc => String(inc.startOfDay), convertedIncidents)
  const maxIncidentsIndex = Object.values(incidentsGroupByDay).reduce((acc, cur) => {
    return Math.max(...cur.map(c => c.index), acc)
  }, 0)
  const seriesIncidents = Object.entries(incidentsGroupByDay)
    .map(([startOfDay, values]) =>
      values.map((value, index) => [
        startOfDay,
        maxNotesInADay + INCIDENT_SPACES + value.index,
        value.truncatedDescription,
        value.friendlyDescription,
        value.timestamp,
      ])
    )
    .flat()

  const seriesIncidentsBars = Object.entries(incidentsGroupByDay).map(([startOfDay, values]) => {
    const incident = values[values.length - 1]
    return [String(incident.startOfDay), maxNotesInADay + INCIDENT_SPACES + incident.index]
  })

  const maxY = maxIncidentsIndex + maxNotesInADay + INCIDENT_SPACES

  return {
    theme: 'agartee-theme',
    tooltip: {
      formatter: (params: any) => {
        let content = ''
        const dateLabel = moment(Number(params.value[params.value.length - 1])).format(CHART_DATE_FORMAT.TIME_ABBR)
        if (params.seriesName === 'note-squares') {
          return dateLabel
        } else {
          content = params.data.value[3]
          return `${dateLabel}<br>${content}`
        }
      },
    },
    grid: GRID,
    xAxis: [
      {
        gridIndex: 0,
        data: timestamps.map(timestamp => String(timestamp)),
        type: 'category',
        axisLine: { show: false },
        axisTick: {
          show: false,
          alignWithLabel: false,
        },
        splitLine: { show: false },
        axisLabel: {
          show: false,
        },
      },
      {
        gridIndex: 1,
        data: timestamps.map(timestamp => String(timestamp)),
        type: 'category',
        axisLine: { show: false },
        axisTick: {
          show: false,
          alignWithLabel: false,
        },
        splitLine: { show: false },
        axisLabel: {
          show: false,
        },
      },
    ],
    yAxis: [
      {
        gridIndex: 0,
        type: 'value',
        splitNumber: 1,
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        min: 0,
        max: maxY,
      },
      {
        gridIndex: 1,
        type: 'value',
        splitNumber: 1,
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        min: 0,
        max: 40,
      },
    ],
    series: [
      {
        // squares for notes
        name: 'note-squares',
        type: 'custom',
        renderItem: customNoteRenderer,
        label: { show: false },
        itemStyle: {
          color: colors.primary,
        },
        // @ts-ignore
        data: seriesNotes,
        xAxisIndex: 0,
        yAxisIndex: 0,
        z: Z_INDEX.NOTES_SQUARE,
      },
      {
        // scatter for incidents
        name: 'incident-scatters',
        type: 'scatter',
        symbolSize: 8,
        label: {
          show: true,
          position: 'right',
          fontSize: 10,
          fontFamily: 'Roboto Mono',
          fontWeight: 'bold',
          backgroundColor: 'rgba(154, 63, 103, 0.08)', // color by burgundy
          color: colors.burgundy,
          padding: INCIDENT_LABEL_PADDING,
          distance: INCIDENT_LABEL_DISTANCE,
          borderRadius: 3,
          formatter: params => {
            return params.data.value[2]
          },
        },
        itemStyle: {
          color: colors.burgundy,
        },
        // @ts-ignore
        data: seriesIncidents.map((data, index) => ({
          value: data,
        })),
        xAxisIndex: 0,
        yAxisIndex: 0,
        z: Z_INDEX.INCIDENT_SCATTER,
      },
      {
        // bar for incidents
        name: 'incident-bars',
        type: 'bar',
        silent: true,
        label: { show: false },
        barWidth: 1,
        itemStyle: {
          color: colors.burgundy,
          opacity: 0.2,
        },
        // @ts-ignore
        data: seriesIncidentsBars,
        xAxisIndex: 0,
        yAxisIndex: 0,
        z: Z_INDEX.INCIDENT_BAR,
      },
      {
        // squares and a line of bottom
        name: 'bottom-line',
        type: 'bar',
        label: { show: false },
        silent: true,
        // @ts-ignore
        data: timestamps.map(timestamp => ({
          value: [String(timestamp), 1],
          itemStyle: {
            color: incidentsGroupByDay[timestamp] ? colors.burgundy : colors.teal,
          },
        })),
        xAxisIndex: 1,
        yAxisIndex: 1,
        z: Z_INDEX.BOTTOMS,
        markLine: {
          silent: true,
          precision: 0,
          symbol: [],
          z: Z_INDEX.BOTTOMS,
          label: {
            show: false,
          },
          lineStyle: {
            color: '#AEBFEC', // primary * opacity 0.3
            type: 'solid',
            width: 4,
          },
          data: [
            // @ts-ignore
            { yAxis: 3 },
          ],
        },
      },
    ],
  }
}
